import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { ProductDto, VariantDto } from '../../types';
import { getCategories, autoCategorize } from '../../data/categoriesStore';
import styles from './ProduitDetail.module.css';

const parsePrice = (val: string): number => parseFloat(val.replace(',', '.')) || 0;

const EMPTY_VARIANT = (productId: number): Omit<VariantDto, 'createdAt' | 'updatedAt'> => ({
  id: 0,
  productId,
  name: '',
  price: 0,
  stock: 0,
  sku: '',
  image: '',
  imageExtension: '',
  option1Value: '',
  option2Value: '',
  option3Value: '',
});

// ── Composant réutilisable upload image ─────────────────────────────────────
interface ImageUploaderProps {
  currentUrl: string;
  previewSrc?: string;
  uploading: boolean;
  uploadStatus: string;
  uploadError: string;
  inputId: string;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteImage: () => void;
  onUrlChange: (url: string, ext: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  currentUrl, previewSrc, uploading, uploadStatus, uploadError,
  inputId, onFileSelect, onDeleteImage, onUrlChange, fileInputRef,
}) => (
  <div className={styles.imageSection}>
    <div className={styles.imagePreviewWrap}>
      {previewSrc || currentUrl ? (
        <img
          src={previewSrc || currentUrl}
          alt="Aperçu"
          className={styles.imagePreview}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <span className={styles.imagePreviewPlaceholder}>📷</span>
      )}
    </div>
    <div className={styles.imageControls}>
      <div>
        <label className={styles.label} style={{ marginBottom: '0.4rem', display: 'block' }}>
          Uploader via FTP
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif"
          onChange={onFileSelect}
          className={styles.filePicker}
          id={inputId}
          disabled={uploading}
        />
        <label htmlFor={inputId} className={styles.btnPickFile} style={{ opacity: uploading ? 0.6 : 1 }}>
          {uploading ? '⏳ Upload en cours...' : '📂 Choisir un fichier (upload auto)'}
        </label>
      </div>

      {uploadStatus && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className={styles.uploadStatus}>{uploadStatus}</span>
          <button onClick={onDeleteImage} className={styles.btnDeleteImage} title="Supprimer">✕</button>
        </div>
      )}
      {uploadError && <span className={styles.uploadError}>{uploadError}</span>}

      <div className={styles.imageUrlRow}>
        <label className={styles.label}>Ou URL manuelle</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            value={currentUrl}
            onChange={(e) => {
              const ext = e.target.value.split('.').pop()?.toLowerCase() ?? '';
              onUrlChange(e.target.value, ext);
            }}
            className={styles.input}
            placeholder="https://eliquidesud.com/Images/img-produits/..."
          />
          {currentUrl && (
            <button onClick={onDeleteImage} className={styles.btnDeleteImage} title="Supprimer">✕</button>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ── Hook upload FTP ──────────────────────────────────────────────────────────
function useImageUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState('');

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    onSuccess: (url: string, ext: string, fileName: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploadStatus('');
    setUploadError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('upload/product-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadStatus(`✓ ${res.data.fileName}`);
      onSuccess(res.data.url, res.data.extension, res.data.fileName);
    } catch (err: any) {
      setUploadError('Erreur upload : ' + (err.response?.data?.message || err.message));
      setPreviewUrl('');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (imageUrl: string, onDone: () => void) => {
    const fileName = imageUrl.split('/').pop() ?? '';
    if (fileName) {
      try {
        await api.delete(`upload/product-image?fileName=${encodeURIComponent(fileName)}`);
      } catch { /* on nettoie quand même */ }
    }
    setPreviewUrl('');
    setUploadStatus('');
    onDone();
  };

  const reset = () => { setPreviewUrl(''); setUploadStatus(''); setUploadError(''); };

  return { fileInputRef, previewUrl, uploading, uploadStatus, uploadError, handleFileSelect, handleDelete, reset };
}

// ── Page principale ──────────────────────────────────────────────────────────
export const ProduitDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<ProductDto | null>(null);
  const [variants, setVariants] = useState<VariantDto[]>([]);
  const [deletedVariantIds, setDeletedVariantIds] = useState<number[]>([]);
  const [addingVariant, setAddingVariant] = useState(false);
  const [newVariant, setNewVariant] = useState<Omit<VariantDto, 'createdAt' | 'updatedAt'> | null>(null);
  const categoryList = useMemo(() => getCategories(), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [generatingMeta, setGeneratingMeta] = useState(false);

  // États string pour les champs prix (accepte virgule et point)
  const [priceStr, setPriceStr] = useState('');
  const [discountPriceStr, setDiscountPriceStr] = useState('');
  const [prixAchatStr, setPrixAchatStr] = useState('');
  const [variantPriceStr, setVariantPriceStr] = useState<Record<number, string>>({});
  const [newVariantPriceStr, setNewVariantPriceStr] = useState('0');

  // Upload image produit
  const productImg = useImageUpload();
  // Upload image variante : map variantId → state
  const [variantUploading, setVariantUploading] = useState<Record<number, boolean>>({});
  const [variantUploadStatus, setVariantUploadStatus] = useState<Record<number, string>>({});
  const [variantUploadError, setVariantUploadError] = useState<Record<number, string>>({});
  const [variantPreview, setVariantPreview] = useState<Record<number, string>>({});
  const variantFileRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [prodRes, varRes] = await Promise.all([
        api.get(`products/${id}`),
        api.get(`variants/product/${id}`),
      ]);
      const prod = prodRes.data?.data ?? prodRes.data;
      setProduct(prod);
      setPriceStr(String(prod.price ?? 0));
      setDiscountPriceStr(prod.discountPrice != null ? String(prod.discountPrice) : '');
      setPrixAchatStr(prod.prixAchat != null ? String(prod.prixAchat) : '');
      const variantData: VariantDto[] = varRes.data?.data ?? varRes.data ?? [];
      setVariants(variantData);
      const priceMap: Record<number, string> = {};
      for (const v of variantData) priceMap[v.id] = String(v.price ?? 0);
      setVariantPriceStr(priceMap);
      productImg.reset();
    } catch (err: any) {
      setError('Erreur de chargement : ' + (err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  const handleGenerateMeta = async () => {
    if (!product) return;
    try {
      setGeneratingMeta(true);
      const res = await api.post(`meta/generate/${product.id}`);
      handleProductChange('metaDescription', res.data.metaDescription);
    } catch (err: any) {
      alert('Erreur génération meta : ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingMeta(false);
    }
  };

  const handleProductChange = (field: keyof ProductDto, value: any) =>
    setProduct((prev) => (prev ? { ...prev, [field]: value } : prev));

  const handleVariantChange = (variantId: number, field: keyof VariantDto, value: any) =>
    setVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, [field]: value } : v)));

  const handleDeleteVariant = (variantId: number) => {
    if (!window.confirm('Supprimer cette variante ?')) return;
    setVariants((prev) => prev.filter((v) => v.id !== variantId));
    if (variantId !== 0) setDeletedVariantIds((prev) => [...prev, variantId]);
  };

  const handleStartAddVariant = () => {
    setNewVariant(EMPTY_VARIANT(Number(id)));
    setNewVariantPriceStr('0');
    setAddingVariant(true);
  };

  const handleNewVariantChange = (field: keyof VariantDto, value: any) =>
    setNewVariant((prev) => (prev ? { ...prev, [field]: value } : prev));

  const handleConfirmAddVariant = async () => {
    if (!newVariant || !newVariant.name.trim()) { alert('Le nom est requis.'); return; }
    try {
      await api.post('variants', newVariant);
      setAddingVariant(false);
      setNewVariant(null);
      const varRes = await api.get(`variants/product/${id}`);
      setVariants(varRes.data?.data ?? varRes.data ?? []);
    } catch (err: any) {
      alert('Erreur création variante : ' + err.message);
    }
  };

  // Upload image variante
  const handleVariantFileSelect = async (variantId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVariantPreview((p) => ({ ...p, [variantId]: URL.createObjectURL(file) }));
    setVariantUploadStatus((p) => ({ ...p, [variantId]: '' }));
    setVariantUploadError((p) => ({ ...p, [variantId]: '' }));
    setVariantUploading((p) => ({ ...p, [variantId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('upload/product-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleVariantChange(variantId, 'image', res.data.url);
      handleVariantChange(variantId, 'imageExtension', res.data.extension);
      setVariantUploadStatus((p) => ({ ...p, [variantId]: `✓ ${res.data.fileName}` }));
    } catch (err: any) {
      setVariantUploadError((p) => ({ ...p, [variantId]: 'Erreur : ' + (err.response?.data?.message || err.message) }));
      setVariantPreview((p) => ({ ...p, [variantId]: '' }));
    } finally {
      setVariantUploading((p) => ({ ...p, [variantId]: false }));
      const ref = variantFileRefs.current[variantId];
      if (ref) ref.value = '';
    }
  };

  const handleDeleteVariantImage = async (variantId: number, imageUrl: string) => {
    const fileName = imageUrl.split('/').pop() ?? '';
    if (fileName) {
      try { await api.delete(`upload/product-image?fileName=${encodeURIComponent(fileName)}`); } catch { }
    }
    handleVariantChange(variantId, 'image', '');
    handleVariantChange(variantId, 'imageExtension', '');
    setVariantPreview((p) => ({ ...p, [variantId]: '' }));
    setVariantUploadStatus((p) => ({ ...p, [variantId]: '' }));
  };

  const handleSave = async () => {
    if (!product) return;
    setSaving(true);
    try {
      await api.put(`products/${product.id}`, product);
      for (const variant of variants) {
        if (variant.id !== 0) await api.put(`variants/${variant.id}`, variant);
      }
      for (const variantId of deletedVariantIds) {
        await api.delete(`variants/${variantId}`);
      }
      setDeletedVariantIds([]);
      alert('Produit sauvegardé !');
      await loadProduct();
    } catch (err: any) {
      alert('Erreur lors de la sauvegarde : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className={styles.loading}>Chargement du produit...</div></Layout>;
  if (error || !product) return <Layout><div className={styles.error}>{error || 'Produit introuvable.'}</div></Layout>;

  return (
    <Layout>
      <div className={styles.container}>

        {/* EN-TÊTE */}
        <div className={styles.header}>
          <button onClick={() => navigate('/produits')} className={styles.btnBack}>← Retour</button>
          <h1 className={styles.title}>{product.name || 'Produit sans nom'}</h1>
          <button onClick={handleSave} disabled={saving} className={styles.btnSave}>
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>

        {/* INFORMATIONS PRODUIT */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Informations générales</h2>
          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Nom</label>
              <input type="text" value={product.name || ''} className={styles.input}
                onChange={(e) => {
                  handleProductChange('name', e.target.value);
                  const result = autoCategorize(e.target.value);
                  if (result && !product.category) {
                    handleProductChange('category', result.category);
                    handleProductChange('subCategory', result.subCategory);
                  }
                }} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Slug</label>
              <input type="text" value={product.slug || ''} className={styles.input}
                onChange={(e) => handleProductChange('slug', e.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Catégorie</label>
              <select value={product.category || ''} className={styles.input}
                onChange={(e) => { handleProductChange('category', e.target.value); handleProductChange('subCategory', ''); }}>
                <option value="">— Choisir —</option>
                {categoryList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Sous-catégorie</label>
              <select value={product.subCategory || ''} className={styles.input}
                onChange={(e) => handleProductChange('subCategory', e.target.value)}>
                <option value="">— Choisir —</option>
                {(categoryList.find((c) => c.name === product.category)?.subCategories ?? []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Prix (€)</label>
              <input type="text" inputMode="decimal" value={priceStr} className={styles.input}
                onChange={(e) => { setPriceStr(e.target.value); handleProductChange('price', parsePrice(e.target.value)); }} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Prix remisé (€)</label>
              <input type="text" inputMode="decimal" value={discountPriceStr} className={styles.input}
                onChange={(e) => { setDiscountPriceStr(e.target.value); handleProductChange('discountPrice', e.target.value.trim() ? parsePrice(e.target.value) : null); }} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Stock</label>
              <input type="number" value={product.stock} className={styles.input}
                onChange={(e) => handleProductChange('stock', parseInt(e.target.value) || 0)} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Prix d'achat (€)</label>
              <input type="text" inputMode="decimal" value={prixAchatStr} className={styles.input}
                placeholder="Ce que vous payez au fournisseur"
                onChange={(e) => { setPrixAchatStr(e.target.value); handleProductChange('prixAchat', e.target.value.trim() ? parsePrice(e.target.value) : null); }} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Code produit fournisseur</label>
              <input type="text" value={product.supplierSku || ''} className={styles.input}
                placeholder="Ex: ACCGLASSZEUSDUAL55"
                onChange={(e) => handleProductChange('supplierSku', e.target.value || null)} />
            </div>
          </div>

          <div className={styles.fieldFull}>
            <label className={styles.label}>Description</label>
            <textarea value={product.description || ''} className={styles.textarea} rows={5}
              onChange={(e) => handleProductChange('description', e.target.value)} />
          </div>
          <div className={styles.fieldFull}>
            <div className={styles.labelRow}>
              <label className={styles.label}>Meta description</label>
              <button
                type="button"
                className={styles.btnGenerateMeta}
                onClick={handleGenerateMeta}
                disabled={generatingMeta}
              >
                {generatingMeta ? '⏳ Génération...' : '✨ Générer avec IA'}
              </button>
            </div>
            <textarea value={product.metaDescription || ''} className={styles.textarea} rows={3}
              onChange={(e) => handleProductChange('metaDescription', e.target.value)} />
          </div>

          <div className={styles.checkboxRow}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={product.bestSeller || false}
                onChange={(e) => handleProductChange('bestSeller', e.target.checked)} />
              Best Seller
            </label>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={product.bonneAffaire || false}
                onChange={(e) => handleProductChange('bonneAffaire', e.target.checked)} />
              Bonne Affaire
            </label>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={product.masquer || false}
                onChange={(e) => handleProductChange('masquer', e.target.checked)} />
              Masquer
            </label>
          </div>
        </div>

        {/* IMAGE PRODUIT */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Image du produit</h2>
          <ImageUploader
            currentUrl={product.image || ''}
            previewSrc={productImg.previewUrl}
            uploading={productImg.uploading}
            uploadStatus={productImg.uploadStatus}
            uploadError={productImg.uploadError}
            inputId="product-img-upload"
            fileInputRef={productImg.fileInputRef}
            onFileSelect={(e) =>
              productImg.handleFileSelect(e, (url, ext) => {
                handleProductChange('image', url);
                handleProductChange('imageExtension', ext);
              })
            }
            onDeleteImage={() =>
              productImg.handleDelete(product.image || '', () => {
                handleProductChange('image', '');
                handleProductChange('imageExtension', '');
              })
            }
            onUrlChange={(url, ext) => {
              handleProductChange('image', url);
              handleProductChange('imageExtension', ext);
              productImg.reset();
            }}
          />
        </div>

        {/* VARIANTES */}
        <div className={styles.card}>
          <div className={styles.variantsHeader}>
            <h2 className={styles.cardTitle}>Variantes ({variants.length})</h2>
            {!addingVariant && (
              <button onClick={handleStartAddVariant} className={styles.btnAdd}>+ Ajouter une variante</button>
            )}
          </div>

          {variants.length === 0 && !addingVariant ? (
            <p className={styles.noData}>Aucune variante pour ce produit.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Nom</th>
                    <th>Prix (€)</th>
                    <th>Stock</th>
                    <th>SKU</th>
                    <th>Option 1</th>
                    <th>Option 2</th>
                    <th>Option 3</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => {
                    const preview = variantPreview[v.id] || v.image || '';
                    const isUploading = variantUploading[v.id] || false;
                    const status = variantUploadStatus[v.id] || '';
                    const errMsg = variantUploadError[v.id] || '';
                    return (
                      <tr key={v.id}>
                        {/* Cellule image variante */}
                        <td style={{ minWidth: 90, textAlign: 'center' }}>
                          <div className={styles.variantImgCell}>
                            {preview ? (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <img src={preview} alt="" className={styles.variantThumb}
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                <button
                                  onClick={() => handleDeleteVariantImage(v.id, v.image || '')}
                                  className={styles.variantImgDelete}
                                  title="Supprimer l'image"
                                >✕</button>
                              </div>
                            ) : (
                              <span className={styles.variantImgPlaceholder}>📷</span>
                            )}
                            <div>
                              <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp,.gif"
                                id={`var-img-${v.id}`}
                                className={styles.filePicker}
                                disabled={isUploading}
                                ref={(el) => { variantFileRefs.current[v.id] = el; }}
                                onChange={(e) => handleVariantFileSelect(v.id, e)}
                              />
                              <label htmlFor={`var-img-${v.id}`} className={styles.btnPickFileSm}
                                style={{ opacity: isUploading ? 0.6 : 1 }}>
                                {isUploading ? '⏳' : '📂'}
                              </label>
                            </div>
                            {status && <div className={styles.variantImgStatus}>{status}</div>}
                            {errMsg && <div className={styles.uploadError}>{errMsg}</div>}
                          </div>
                        </td>
                        <td>
                          <input type="text" value={v.name || ''} className={styles.cellInput}
                            onChange={(e) => handleVariantChange(v.id, 'name', e.target.value)} />
                        </td>
                        <td>
                          <input type="text" inputMode="decimal" value={variantPriceStr[v.id] ?? String(v.price)} className={styles.cellInputSm}
                            onChange={(e) => { setVariantPriceStr(p => ({ ...p, [v.id]: e.target.value })); handleVariantChange(v.id, 'price', parsePrice(e.target.value)); }} />
                        </td>
                        <td>
                          <input type="number" value={v.stock} className={styles.cellInputSm}
                            onChange={(e) => handleVariantChange(v.id, 'stock', parseInt(e.target.value) || 0)} />
                        </td>
                        <td>
                          <input type="text" value={v.sku || ''} className={styles.cellInputSm}
                            onChange={(e) => handleVariantChange(v.id, 'sku', e.target.value)} />
                        </td>
                        <td>
                          <input type="text" value={v.option1Value || ''} className={styles.cellInputSm}
                            onChange={(e) => handleVariantChange(v.id, 'option1Value', e.target.value)} />
                        </td>
                        <td>
                          <input type="text" value={v.option2Value || ''} className={styles.cellInputSm}
                            onChange={(e) => handleVariantChange(v.id, 'option2Value', e.target.value)} />
                        </td>
                        <td>
                          <input type="text" value={v.option3Value || ''} className={styles.cellInputSm}
                            onChange={(e) => handleVariantChange(v.id, 'option3Value', e.target.value)} />
                        </td>
                        <td>
                          <button onClick={() => handleDeleteVariant(v.id)} className={styles.btnDelete} title="Supprimer">×</button>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Ligne d'ajout */}
                  {addingVariant && newVariant && (
                    <tr className={styles.newRow}>
                      <td></td>
                      <td>
                        <input type="text" placeholder="Nom *" value={newVariant.name} autoFocus
                          onChange={(e) => handleNewVariantChange('name', e.target.value)}
                          className={styles.cellInput} />
                      </td>
                      <td>
                        <input type="text" inputMode="decimal" placeholder="0" value={newVariantPriceStr}
                          onChange={(e) => { setNewVariantPriceStr(e.target.value); handleNewVariantChange('price', parsePrice(e.target.value)); }}
                          className={styles.cellInputSm} />
                      </td>
                      <td>
                        <input type="number" placeholder="0" value={newVariant.stock}
                          onChange={(e) => handleNewVariantChange('stock', parseInt(e.target.value) || 0)}
                          className={styles.cellInputSm} />
                      </td>
                      <td>
                        <input type="text" placeholder="SKU" value={newVariant.sku || ''}
                          onChange={(e) => handleNewVariantChange('sku', e.target.value)}
                          className={styles.cellInputSm} />
                      </td>
                      <td>
                        <input type="text" placeholder="Option 1" value={newVariant.option1Value || ''}
                          onChange={(e) => handleNewVariantChange('option1Value', e.target.value)}
                          className={styles.cellInputSm} />
                      </td>
                      <td>
                        <input type="text" placeholder="Option 2" value={newVariant.option2Value || ''}
                          onChange={(e) => handleNewVariantChange('option2Value', e.target.value)}
                          className={styles.cellInputSm} />
                      </td>
                      <td>
                        <input type="text" placeholder="Option 3" value={newVariant.option3Value || ''}
                          onChange={(e) => handleNewVariantChange('option3Value', e.target.value)}
                          className={styles.cellInputSm} />
                      </td>
                      <td className={styles.newRowActions}>
                        <button onClick={handleConfirmAddVariant} className={styles.btnConfirm} title="Confirmer">✓</button>
                        <button onClick={() => { setAddingVariant(false); setNewVariant(null); }} className={styles.btnCancel} title="Annuler">×</button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
};
